import {ParsedUrlQueryInput} from 'querystring';
import {useNavigate, useRouter} from '@tanstack/react-router';
import queryString from 'query-string';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ParseOptions} from 'query-string';

// Store router instance to avoid circular dependency
let routerInstance: any = null;

export function setRouterInstance(router: any) {
  routerInstance = router;
}

function getRouter() {
  if (!routerInstance) {
    throw new Error('Router instance not set. Call setRouterInstance(router) first.');
  }
  return routerInstance;
}

function peekRouter() {
  return routerInstance;
}

/**
 * All routes in the application. When adding new pages, add the route here.
 */
export enum Route {
  authCallback = '/auth/callback',
  archive = '/archive',
  archiveImage = '/archive/image',
  simsearch = '/simsearch',
  simsearchImage = '/simsearch/image',
  index = '/',
  heatmap = '/heatmap',
  changelog = '/changelog',
  modelAddSamples = '/model/add-samples',
  modelAddSamplesImage = '/model/add-samples/image',
  modelFinetune = '/model/finetune',
  modelFinetuneImage = '/model/finetune/image',
  modelFinetuneDeploymentSettings = '/model/finetune/deployment-settings',
  modelFinetuneImproveDataset = '/model/finetune/improve-dataset',
  modelFinetuneImproveDatasetLoading = '/model/finetune/improve-dataset-loading',
  modelFinetuneGenerateDataLoading = '/model/finetune/generate-data-loading',
  modelFinetuneGenerateDataSuccess = '/model/finetune/generate-data-success',
  modelLabelDistribution = '/model/statistics/label-distribution',
  modelLabelNoise = '/model/statistics/label-noise',
  modelNew = '/models/new',
  modelTraining = '/model/training',
  models = '/models',
  modelSamples = '/model/samples',
  modelSamplesDefectStudio = '/model/samples/defect-studio',
  modelSamplesImage = '/model/samples/image',
  modelSamplesSmartSelect = '/model/samples/smart-select',
  modelSamplesSmartSelectResults = '/model/samples/smart-select-results',
  modelSettings = '/model/settings',
  modelStatistics = '/model/statistics',
  monitor = '/monitor',
  notFound = '/404',
  projects = '/projects',
  settingsGeneral = '/settings/general',
  settingsAdminConfig = '/settings/adminConfig',
  settingsAutomaticReports = '/settings/automaticReports',
  settingsApiTokens = '/settings/apiTokens',
  settingsDevices = '/settings/devices',
  settingsNotifications = '/settings/notifications',
  settingsPerspectives = '/settings/perspectives',
  settingsShifts = '/settings/shifts',
  settingsUsers = '/settings/users',
  tasks = '/tasks',
  tasksLiveSession = '/tasks/live-session',
  tasksSession = '/tasks/session',
  apiUnavailable = '/503',
  noProjects = '/no-projects',
  invalidProject = '/invalid-project',
  deploy = '/deploy',
  qualityAgent = '/quality-agent',
}

export enum LandingPageRoutes {
  Monitor = 'MONITOR',
  Tasks = 'TASKS',
  Archive = 'ARCHIVE',
  Models = 'MODELS',
}

/**
 * Where users are sent when no start page is configured, or when the configured
 * one is no longer reachable (see the `/tasks` routes, which redirect here).
 */
export const defaultLandingPageRoute = Route.archive;

export const projectIdQueryParam = 'projectID';
export const orgIdQueryParam = 'orgID';
export const modelIdQueryParam = 'modelID';
/** The defect class the Defect Studio files its variants under (`/model/samples/defect-studio`). */
export const defectStudioLabelIdQueryParam = 'labelID';
export const imageIdQueryParam = 'imageID';
export const sessionTagQueryParam = 'sessionTag';
export const loginStatusQueryParam = 'status';
export const loginRedirectQueryParam = 'redirect';
export const confusionMatrixCellIdQueryParam = 'confusionMatrixCellID';
export const originalModelIdQueryParam = 'originalModelID';
export const inspectionTypeQueryParam = 'inspection_type';
export const imageBrowserHumanLabelQueryParam = 'humanLabel';
export const imageBrowserMachineLabelQueryParam = 'machineLabel';
export const imageBrowserAreaSizeQueryParam = 'areaSize';
export const imageBrowserConfidenceThresholdQueryParam = 'confidenceThreshold';
export const imageBrowserConfidenceThresholdNotRoundedQueryParam = 'confidenceThresholdNotRounded';
export const imageBrowserDatasetIdQueryParam = 'datasetID';
export const imageBrowserActiveViewQueryParam = 'imageBrowserActiveView';
export const imageBrowserPageQueryParam = 'imageBrowserPage';
export const imageBrowserFromCmGalleryQueryParam = 'fromCmGallery';
export const imagesOverviewViewModeQueryParam = 'imagesOverviewViewMode';
export const showTrainingHistoryQueryParam = 'showTrainingHistory';
export const finetunePageActiveTabQueryParam = 'finetunePageActiveTab';
export const finetunePageActiveTabImageQueryParam = 'finetunePageActiveTabImage';
export const finetuneMetricsInterpretationQueryParam = 'finetuneMetricsInterpretation';
export const evaluationPipelineIdQueryParam = 'evaluationPipelineID';
export const trainingPipelineIdQueryParam = 'trainingPipelineID';
export const evaluationCellPredictedClassQueryParam = 'evaluationCellPredictedClass';
export const withUnstablePredictionsQueryParam = 'withUnstablePredictions';
export const similaritySearchQueryParam = 'similaritySearch';
export const modelsOverviewActiveTabQueryParam = 'modelsOverviewActiveTab';
export const monitorTabActiveTabQueryParam = 'monitorTabActiveTab';
export const keypointSelectedDistanceQueryParam = 'keypointSelectedDistance';
export const keypointFinetuneQueryParam = 'keypointFinetune';
export const keypointEvaluationQueryParam = 'keypointEvaluation';
export const itemsPerPageQueryParam = 'itemsPerPage';
export const imageOrderByQueryParam = 'orderBy';
export const devicesQueryParam = 'devices';
export const humanAnnotationLabelsQueryParam = 'humanAnnotationLabels';
export const machinePredictionLabelsQueryParam = 'machinePredictionLabels';
export const timeFrameQueryParam = 'timeframe';
export const productTypeQueryParam = 'productType';
export const withNoImagesQueryParam = 'withNoImages';
export const listViewGroupByQueryParam = 'listViewGroupBy';
export const deployOverviewTabQueryParam = 'deployOverviewTab';
export const returnPathQueryParam = 'returnPath';
export const automlPipelineIdQueryParam = 'automlPipelineId';
/**
 * Id of the pipeline that produced the currently shown set of images (curation run,
 * training pipeline behind a confusion matrix cell, ...). Carried purely so annotation
 * telemetry can attribute annotations to the run the user came from.
 */
export const viewPipelineIdQueryParam = 'viewPipelineId';
export const serialNumberQueryParam = 'serialNumber';

// ============================================================================
// CENTRALIZED SEARCH PARAMS SCHEMAS
// ============================================================================

/**
 * Search param validator helper
 */
function validateSearchParam<T = string>(value: unknown, fallback?: T): T | undefined {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  return value as T;
}

/**
 * Common search params used across multiple routes
 */
export const CommonSearchParams = {
  projectID: (search: Record<string, unknown>) => validateSearchParam<string>(search.projectID),
  orgID: (search: Record<string, unknown>) => validateSearchParam<string>(search.orgID),
  modelID: (search: Record<string, unknown>) => validateSearchParam<string>(search.modelID),
  imageID: (search: Record<string, unknown>) => validateSearchParam<string>(search.imageID),
  inspectionType: (search: Record<string, unknown>) => validateSearchParam<string>(search.inspection_type),
  imagesOverviewViewMode: (search: Record<string, unknown>) =>
    validateSearchParam<string>(search.imagesOverviewViewMode),
};

/**
 * URL query keys accepted by the archive route. Keep in sync with {@link ArchiveSearchParams.validate}.
 */
const archiveRouteSearchParamKeys = [
  projectIdQueryParam,
  modelIdQueryParam,
  inspectionTypeQueryParam,
  serialNumberQueryParam,
  imagesOverviewViewModeQueryParam,
] as const;

export const archiveAllowedSearchParamKeys = new Set<string>(archiveRouteSearchParamKeys);

/**
 * Archive route search params schema
 */
export const ArchiveSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    projectID: validateSearchParam<string>(search[projectIdQueryParam]),
    orgID: CommonSearchParams.orgID(search),
    serialNumber: validateSearchParam<string>(search[serialNumberQueryParam]),
    modelID: validateSearchParam<string>(search[modelIdQueryParam]),
    inspectionType: validateSearchParam<string>(search[inspectionTypeQueryParam]),
    imagesOverviewViewMode: validateSearchParam<string>(search[imagesOverviewViewModeQueryParam]),
  }),
};

export const ModelTrainingSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    projectID: CommonSearchParams.projectID(search),
    modelID: CommonSearchParams.modelID(search),
    inspection_type: CommonSearchParams.inspectionType(search),
    showTrainingHistory: validateSearchParam<string>(search.showTrainingHistory),
  }),
};

/**
 * Login route search params schema
 */
export const LoginSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    redirect: validateSearchParam<string>(search.redirect),
    status: validateSearchParam<string>(search.status),
  }),
};

/**
 * Models route search params schema
 */
export const ModelsSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    modelsOverviewActiveTab: validateSearchParam<string>(search.modelsOverviewActiveTab),
    projectID: CommonSearchParams.projectID(search),
    orgID: CommonSearchParams.orgID(search),
  }),
};

/**
 * Model Samples route search params schema
 */
export const ModelSamplesSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    projectID: CommonSearchParams.projectID(search),
    orgID: CommonSearchParams.orgID(search),
    modelID: CommonSearchParams.modelID(search),
    inspection_type: CommonSearchParams.inspectionType(search),
    imagesOverviewViewMode: CommonSearchParams.imagesOverviewViewMode(search),
  }),
};

/**
 * Tasks route search params schema
 */
export const TasksSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    projectID: CommonSearchParams.projectID(search),
    orgID: CommonSearchParams.orgID(search),
    modelID: CommonSearchParams.modelID(search),
    inspectionType: CommonSearchParams.inspectionType(search),
  }),
};

/**
 * No Projects route search params schema
 */
export const NoProjectsSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    projectID: CommonSearchParams.projectID(search),
    orgID: CommonSearchParams.orgID(search),
  }),
};

/**
 * Generic route search params schema (for routes that only need projectID)
 */
export const GenericProjectSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    projectID: CommonSearchParams.projectID(search),
    orgID: CommonSearchParams.orgID(search),
  }),
};

export const MonitorSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    projectID: CommonSearchParams.projectID(search),
    orgID: CommonSearchParams.orgID(search),
    isOkHidden: validateSearchParam<string>(search.isOkHidden),
    format: validateSearchParam<string>(search.format),
    timeframe: validateSearchParam<string>(search.timeframe),
    shifts: validateSearchParam<string>(search.shifts),
    productTypes: validateSearchParam<string>(search.productTypes),
    devices: validateSearchParam<string>(search.devices),
    meta: validateSearchParam<string>(search.meta),
    binSize: validateSearchParam<string>(search.binSize),
    highlightStart: validateSearchParam<string>(search.highlightStart),
    highlightEnd: validateSearchParam<string>(search.highlightEnd),
  }),
};

export const SettingsSearchParams = {
  validate: (search: Record<string, unknown>) => ({
    projectID: CommonSearchParams.projectID(search),
    orgID: CommonSearchParams.orgID(search),
  }),
};

// Type exports for TypeScript support
export type ArchiveSearchParamsType = ReturnType<typeof ArchiveSearchParams.validate>;
export type LoginSearchParamsType = ReturnType<typeof LoginSearchParams.validate>;
export type ModelsSearchParamsType = ReturnType<typeof ModelsSearchParams.validate>;
export type ModelSamplesSearchParamsType = ReturnType<typeof ModelSamplesSearchParams.validate>;
export type TasksSearchParamsType = ReturnType<typeof TasksSearchParams.validate>;
export type NoProjectsSearchParamsType = ReturnType<typeof NoProjectsSearchParams.validate>;
export type GenericProjectSearchParamsType = ReturnType<typeof GenericProjectSearchParams.validate>;
export type SettingsSearchParamsType = ReturnType<typeof SettingsSearchParams.validate>;

export type RouteOptions = {
  query?: ParsedUrlQueryInput;
  keepProjectIdQueryParam?: boolean;
  keepOrgIdQueryParam?: boolean;
  keepModelIdQueryParam?: boolean;
  keepTrainingPipelineIdQueryParam?: boolean;
};

const defaultRouteOptions: RouteOptions = {
  keepProjectIdQueryParam: true,
  keepOrgIdQueryParam: true,
  keepModelIdQueryParam: true,
  keepTrainingPipelineIdQueryParam: false,
};

/**
 * Takes a list of query string keys to retrieve and returns an
 * object of the keys and their values found in the URL query string.
 *
 * - Keys not found in the URL query string are omitted from the returned object
 * - If a key occurs more than once in the URL query string, the first value is used.
 */
export function getQueryParams(paramsToGet: string[]): Record<string, string> {
  const currentLocation = peekRouter()?.state?.location;
  if (!currentLocation) {
    return {};
  }
  const queryString = currentLocation.href.split('?')[1];
  const searchParams = new URLSearchParams(queryString);
  const allParams = Object.fromEntries(searchParams.entries());
  if (paramsToGet) {
    return Object.fromEntries(
      Object.entries(allParams).filter(([key, value]) => paramsToGet.includes(key) && value !== undefined),
    );
  }
  return allParams;
}

export function getMeasuringRoute(modelId: string) {
  const path = `/model/${modelId}/measuring`;
  const scope = new URLSearchParams(getQueryParams([orgIdQueryParam, projectIdQueryParam])).toString();
  return scope ? `${path}?${scope}` : path;
}

/**
 * Get all query params as configured by the provided {@link RouteOptions}.
 */
function getQuery(options?: RouteOptions): ParsedUrlQueryInput {
  const routeOptions = {
    ...defaultRouteOptions,
    ...options,
  };

  const globalQueryParams = getQueryParams([
    projectIdQueryParam,
    orgIdQueryParam,
    modelIdQueryParam,
    inspectionTypeQueryParam,
    trainingPipelineIdQueryParam,
  ]);
  const query: ParsedUrlQueryInput = {...globalQueryParams, ...routeOptions.query};

  if (query[modelIdQueryParam] && !routeOptions.keepModelIdQueryParam) {
    delete query[modelIdQueryParam];
  }

  if (query[projectIdQueryParam] && !routeOptions.keepProjectIdQueryParam) {
    delete query[projectIdQueryParam];
  }

  if (query[orgIdQueryParam] && !routeOptions.keepOrgIdQueryParam) {
    delete query[orgIdQueryParam];
  }

  // we reuse keepModelIdQueryParam because it makes perfect sense :ok:
  if (query[inspectionTypeQueryParam] && !routeOptions.keepModelIdQueryParam) {
    delete query[inspectionTypeQueryParam];
  }

  // Clear trainingPipelineIdQueryParam unless explicitly kept
  if (query[trainingPipelineIdQueryParam] && !routeOptions.keepTrainingPipelineIdQueryParam) {
    delete query[trainingPipelineIdQueryParam];
  }

  // Remove null or undefined values
  for (const key in query) {
    const value = query[key];
    if (value === null || value === undefined) {
      delete query[key];
    }
  }

  return query;
}
export function getRoute(route: Route, options?: RouteOptions) {
  const urlSearchParams = new URLSearchParams();
  const query = getQuery(options);

  for (const key in query) {
    const value = query[key];
    if (typeof value === 'string') {
      urlSearchParams.set(key, value);
    }
  }

  if (urlSearchParams.toString() === '') {
    return route;
  }
  return `${route}?${urlSearchParams.toString()}`;
}

export async function pushRoute(pathname: Route | string, options?: RouteOptions): Promise<void> {
  const router = getRouter();
  const search = getQuery(options);
  await router.navigate({to: pathname as any, search: search as any});
}

export async function replaceRoute(pathname: Route | string, options?: RouteOptions): Promise<void> {
  const router = getRouter();
  const search = getQuery(options);
  await router.navigate({to: pathname as any, search: search as any, replace: true});
}

export async function popPathname(method: 'push' | 'replace', options?: RouteOptions): Promise<void> {
  const router = getRouter();
  const currentLocation = router.state.location;
  const pathnameParts = currentLocation.pathname.split('/');
  if (pathnameParts.length <= 1) {
    return;
  }

  const pathname = pathnameParts.slice(0, pathnameParts.length - 1).join('/');
  const search = getQuery(options);

  if (method === 'push') {
    await router.navigate({to: pathname as any, search: search as any});
  } else if (method === 'replace') {
    await router.navigate({to: pathname as any, search: search as any, replace: true});
  }
}

export const queryStringParseOptions: ParseOptions = {
  parseBooleans: true,
  arrayFormat: 'separator',
  arrayFormatSeparator: '|',
};

export type QueryStringValue = undefined | string | boolean | null | Array<string | null>;

function getLocationHref(router: ReturnType<typeof useRouter> | null | undefined): string {
  return router?.state?.location?.href ?? (typeof window !== 'undefined' ? window.location.href : '');
}

export function useQueryParam(
  param: string,
  defaultValue?: QueryStringValue,
): [value: QueryStringValue, updateValue: (value: QueryStringValue, usePush?: boolean) => void] {
  const navigate = useNavigate();
  const router = useRouter();

  // Use router.state.location with subscription for reactivity
  // This works even when router context isn't fully initialized
  const [locationHref, setLocationHref] = useState<string>(() => getLocationHref(router));

  // Subscribe to router state changes to make it reactive
  // Use a ref to track the previous href and update when it changes
  const prevHrefRef = useRef<string>(locationHref);

  useEffect(() => {
    if (!router) {
      return;
    }

    const updateFromRouter = () => {
      const currentHref = getLocationHref(router);

      if (currentHref !== prevHrefRef.current) {
        prevHrefRef.current = currentHref;
        setLocationHref(currentHref);
      }
    };

    // Subscribe to navigation events
    const unsubscribeBefore = router.subscribe('onBeforeNavigate', () => {
      // Update after navigation completes
      setTimeout(updateFromRouter, 0);
    });

    const unsubscribeLoad = router.subscribe('onLoad', () => {
      updateFromRouter();
    });

    // Initial update
    updateFromRouter();

    // Also check periodically for changes (fallback for cases where events don't fire)
    // Use a longer interval to avoid performance issues
    const intervalId = setInterval(updateFromRouter, 200);

    return () => {
      unsubscribeBefore();
      unsubscribeLoad();
      clearInterval(intervalId);
    };
  }, [router]); // Don't include locationHref to prevent infinite loop

  const {query} = queryString.parseUrl(locationHref, queryStringParseOptions);

  // Call useState unconditionally (React hooks must be called in the same order)
  const [value, setValue] = useState(() => {
    // If router context isn't available, use defaultValue
    if (!navigate || !router) {
      return defaultValue;
    }
    return query[param] || defaultValue;
  });

  // Call useCallback unconditionally (React hooks must be called in the same order)
  const updateValue = useCallback(
    (newValue: QueryStringValue, usePush?: boolean) => {
      // Guard against navigate/router being null (can happen in test environments)
      if (!navigate || !router) {
        return;
      }

      if (value === newValue) {
        return;
      }

      // Update local state immediately for better reactivity
      setValue(newValue);

      (navigate as any)({
        search: (prev: any) => {
          const updated = {...prev};
          // If newValue is undefined or null, explicitly remove the param
          if (newValue === undefined || newValue === null) {
            delete updated[param];
          } else {
            updated[param] = newValue;
          }
          return updated;
        },
        replace: !usePush,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [param, value, locationHref, navigate, router],
  );

  // Call useEffect unconditionally (React hooks must be called in the same order)
  useEffect(() => {
    // Guard against router being null (can happen in test environments)
    if (!router) {
      return;
    }
    // Re-parse query from locationHref to ensure we have the latest values
    const {query: currentQuery} = queryString.parseUrl(locationHref, queryStringParseOptions);
    const currentParamValue = currentQuery[param];

    if (currentParamValue === undefined) {
      // Param is not present in URL
      if (defaultValue !== undefined) {
        // Set the default value if provided
        setValue((prevValue) => (prevValue !== defaultValue ? defaultValue : prevValue));
      } else {
        // No default value, so update state to undefined to reflect param removal
        setValue((prevValue) => (prevValue !== undefined ? undefined : prevValue));
      }
    } else if (currentParamValue !== value) {
      // Update the local state value whenever the relevant Router query param is updated
      // Only update if the value actually changed
      setValue(currentParamValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, param, locationHref, router]); // Removed 'value' from deps to prevent unnecessary updates

  // Guard against navigate/router being null (can happen in test environments)
  // If router context isn't available, provide a no-op update function
  if (!navigate || !router) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return [value, () => {}];
  }

  return [value, updateValue];
}

export type QueryStringObject = Record<string, QueryStringValue>;
export function useQueryParams(observedParams: string[]): [QueryStringObject, (params: QueryStringObject) => void] {
  const navigate = useNavigate();
  const router = useRouter();
  const currentLocationHref = getLocationHref(router);
  const queryParams = useMemo(() => {
    const {query} = queryString.parseUrl(currentLocationHref, queryStringParseOptions);
    for (const key in query) {
      if (!observedParams.includes(key)) {
        delete query[key];
      }
    }
    return query;
  }, [observedParams, currentLocationHref]);

  const updateQueryParams = useCallback(
    (params: QueryStringObject) => {
      if (!navigate || !router) {
        return;
      }
      (navigate as any)({
        search: (prev: any) => {
          const updated: QueryStringObject = {...prev};

          for (const key in params) {
            const value = params[key];

            // Remove param when value is undefined, null, empty string or empty array
            if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
              // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
              delete updated[key];
            } else {
              updated[key] = value;
            }
          }

          return updated;
        },
        replace: true,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [navigate, router],
  );

  return [queryParams, updateQueryParams];
}
